'use client';

import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Avatar as MuiAvatar,
  Typography,
  Stack,
  IconButton,
  Button,
  Divider,
  Container,
  Grid,
  useMediaQuery,
  useTheme,
  alpha,
  Chip,
  Paper,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  CameraAlt,
  Person,
  Business,
  CheckCircle,
  ChevronRight,
  Verified,
  SupportAgent,
  Security,
  Payment,
  History,
  Star,
  Logout,
} from '@mui/icons-material';
import EditFieldModal from './EditFieldModal';
import InlineEditableField from './InlineEditableField';
import UserTypeSelector from './UserTypeSelector';
import BusinessFields from './BusinessFields';
import LogoutConfirmationDialog from '@/components/common/LogoutConfirmationDialog';
import { LayoutContainer } from '../../kit';
import { themeColors } from '../../theme/theme';
import { PROFILE_FIELDS, PHONE_CONFIG } from './config';
import { validateName, validateEmail, validatePhone } from './validations';
import { useProfile } from './hooks/useProfile';
import { brandingConfig } from '@/config/branding';
import { useTranslation } from '../../kit/useTranslation';

// Create motion components
const MotionBox = motion.create(Box);
const MotionPaper = motion.create(Paper);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const heroVariants = {
  hidden: { opacity: 0, y: -30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const avatarVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20,
      delay: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
    },
  },
};

export default function ProfilePage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const {
    user,
    editModal,
    editLoading,
    userTypeDetails,
    loading,
    errors,
    successMessage,
    showLogoutDialog,
    loggingOut,
    handleEditClick,
    handleEditClose,
    handleEditSave,
    handleFieldSave,
    handleUserTypeChange,
    handleLogout,
    confirmLogout,
    cancelLogout,
    clearError,
  } = useProfile();
  const [editingUserType, setEditingUserType] = React.useState(!user?.userType);
  React.useEffect(() => {
    setEditingUserType(!user?.userType);
  }, [user?.userType]);

  const isBusiness = String(user?.userType || '').toLowerCase() === 'business';
  const primaryColor = isBusiness ? '#f59e0b' : '#000000';
  const gradientBg =
    theme.palette.mode === 'dark'
      ? isBusiness
        ? 'linear-gradient(135deg, #222426 0%, #2f3332 50%, #222426 100%)'
        : 'linear-gradient(135deg, #0f1112 0%, #1f2223 50%, #0f1112 100%)'
      : isBusiness
        ? `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.85)} 100%)`
        : 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 50%, #ffffff 100%)';

  // Determine readable text color over hero background
  const heroTextColor =
    theme.palette.mode === 'dark' || isBusiness ? '#fff' : theme.palette.text.primary;

  // Chip colors adapt based on background for legibility
  const chipBg =
    theme.palette.mode === 'dark' || isBusiness
      ? alpha('#fff', 0.18)
      : alpha(theme.palette.grey[300], 0.95);
  const chipColor =
    theme.palette.mode === 'dark' || isBusiness ? '#fff' : theme.palette.text.primary;

  // Profile completion percentage
  const profileFields = [user?.userName, user?.userEmail, user?.userPhoneNumber, user?.userType];
  const completedFields = profileFields.filter(Boolean).length;
  const completionPercentage = Math.round((completedFields / profileFields.length) * 100);

  const quickActions = [
    {
      icon: SupportAgent,
      label: t('profile.helpSupport'),
      description: t('profile.getAssistance'),
      href: '/support',
      color: '#3B82F6',
    },
    {
      icon: Security,
      label: t('profile.privacySecurity'),
      description: t('profile.manageSettings'),
      href: '#',
      color: '#8B5CF6',
    },
    {
      icon: Payment,
      label: t('profile.paymentMethods'),
      description: t('profile.addOrUpdate'),
      href: '/wallet',
      color: '#10B981',
    },
    {
      icon: History,
      label: t('profile.orderHistory'),
      description: t('profile.viewPastOrders'),
      href: '/history',
      color: '#F59E0B',
    },
  ];

  return (
    <LayoutContainer>
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 6 }}>
        {/* Success Message Toast */}
        <Fade in={!!successMessage}>
          <Box
            sx={{
              position: 'fixed',
              top: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300,
              bgcolor: '#10b981',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CheckCircle sx={{ fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600}>
              {successMessage}
            </Typography>
          </Box>
        </Fade>

        {/* Hero Header with Avatar */}
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={heroVariants}
          sx={{
            background: gradientBg,
            pt: { xs: 4, md: 5 },
            pb: { xs: 10, md: 12 },
            px: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Animated decorative circles */}
          <MotionBox
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.08, 0.12, 0.08],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 250,
              height: 250,
              borderRadius: '50%',
              bgcolor: '#fff',
            }}
          />
          <MotionBox
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.05, 0.08, 0.05],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5,
            }}
            sx={{
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 180,
              height: 180,
              borderRadius: '50%',
              bgcolor: '#fff',
            }}
          />
          <MotionBox
            animate={{
              y: [0, -10, 0],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            sx={{
              position: 'absolute',
              top: '40%',
              left: '20%',
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: alpha('#fff', 0.3),
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Stack
              // On small screens keep avatar to the left and text to the right
              direction={{ xs: 'row', sm: 'row' }}
              spacing={2}
              alignItems="center"
              sx={{ width: '100%', alignItems: 'center', gap: 2 }}
            >
              {/* Avatar with camera overlay */}
              <MotionBox variants={avatarVariants} sx={{ position: 'relative' }}>
                <MuiAvatar
                  sx={{
                    width: { xs: 84, md: 130 },
                    height: { xs: 84, md: 130 },
                    bgcolor:
                      theme.palette.mode === 'dark' || isBusiness
                        ? alpha('#fff', 0.18)
                        : alpha(theme.palette.grey[200], 0.8),
                    fontSize: { xs: '1.25rem', md: '3rem' },
                    fontWeight: 700,
                    color: heroTextColor,
                    border: { xs: '1px solid', md: '4px solid' },
                    borderColor:
                      theme.palette.mode === 'dark' || isBusiness
                        ? alpha('#fff', 0.3)
                        : alpha(theme.palette.grey[300], 0.9),
                    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    backdropFilter: 'blur(6px)',
                    mr: { xs: 1, sm: 3 },
                  }}
                >
                  {user.userName?.[0]?.toUpperCase() || 'U'}
                </MuiAvatar>
                {/* <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    bgcolor: '#fff',
                    width: 38,
                    height: 38,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: '#f3f4f6', transform: 'scale(1.1)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <CameraAlt sx={{ fontSize: 18, color: '#374151' }} />
                </IconButton> */}
              </MotionBox>

              {/* User info */}
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                sx={{ textAlign: { xs: 'center', sm: 'left' } }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '1.25rem', md: '2.25rem' },
                      fontWeight: 600,
                      color: heroTextColor,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {user.userName || t('profile.welcome')}
                  </Typography>
                  {user.userEmail && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: 'spring' }}
                    >
                      <Verified sx={{ color: alpha(heroTextColor, 0.9), fontSize: 26 }} />
                    </motion.div>
                  )}
                </Stack>
                <Typography
                  sx={{
                    fontSize: '1.05rem',
                    color: alpha(heroTextColor, 0.75),
                    mt: 0.5,
                    textAlign: { xs: 'left', sm: 'left' },
                  }}
                >
                  {user.userEmail || t('profile.completeProfileMsg')}
                </Typography>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Chip
                    icon={
                      isBusiness ? (
                        <Business sx={{ fontSize: 16, color: 'inherit' }} />
                      ) : (
                        <Person sx={{ fontSize: 16, color: 'inherit' }} />
                      )
                    }
                    label={isBusiness ? t('profile.businessAccount') : t('profile.personalAccount')}
                    sx={{
                      mt: 1.5,
                      bgcolor: chipBg,
                      color: chipColor,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      backdropFilter: 'blur(6px)',
                      '& .MuiChip-icon': { color: chipColor },
                    }}
                  />
                </motion.div>
                {user.userId && (
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      color: alpha(heroTextColor, 0.55),
                      mt: 0.5,
                      fontFamily: 'monospace',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('profile.id', { id: user.userId })}
                  </Typography>
                )}
              </MotionBox>
            </Stack>
          </Container>
        </MotionBox>

        {/* Main Content - Overlapping cards */}
        <Container
          maxWidth="lg"
          sx={{
            mt: { xs: -6, md: -8 },
            px: { xs: 0, sm: 2, md: 3 },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <MotionBox initial="hidden" animate="visible" variants={containerVariants}>
            {/* Profile Completion Card */}
            {completionPercentage < 100 && (
              <MotionPaper
                variants={cardVariants}
                elevation={0}
                sx={{
                  borderRadius: { xs: 0, sm: 4 },
                  border: '1px solid',
                  borderColor: '#e5e7eb',
                  bgcolor: '#fff',
                  mb: { xs: 2, sm: 3 },
                  mx: { xs: 2, sm: 0 },
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  width: { xs: 'calc(100% - 32px)', sm: '100%' },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.7)} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Star sx={{ color: '#fff', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#1f2937',
                            fontSize: '1rem',
                          }}
                        >
                          {t('profile.completeProfile')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {t('profile.percentComplete', { percent: completionPercentage })}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        color: primaryColor,
                      }}
                    >
                      {completionPercentage}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={completionPercentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(primaryColor, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.8)} 100%)`,
                      },
                    }}
                  />
                </Box>
              </MotionPaper>
            )}

            <Grid
              container
              sx={{ xs: 0, sm: 3, display: isDesktop ? 'flex' : 'block' }}
              spacing={3}
            >
              {/* Left Column - Personal & Account Info */}
              <Grid item xs={12} md={5}>
                {/* Personal Information Card */}
                <MotionPaper
                  variants={cardVariants}
                  elevation={0}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  sx={{
                    borderRadius: { xs: 0, sm: 4 },
                    border: '1px solid',
                    borderColor: '#e5e7eb',
                    bgcolor: '#fff',
                    mb: { xs: 2, sm: 3 },
                    mx: { xs: 2, sm: 0 },
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.7)} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Person sx={{ color: '#fff', fontSize: 22 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '1.15rem',
                          fontWeight: 700,
                          color: '#1f2937',
                        }}
                      >
                        {t('profile.personalInfo')}
                      </Typography>
                    </Stack>

                    <Stack spacing={2.5}>
                      <InlineEditableField
                        label={PROFILE_FIELDS.NAME.label}
                        value={user.userName}
                        validate={validateName}
                        onSave={(val) => handleFieldSave('userName', val)}
                      />
                      <InlineEditableField
                        label={PROFILE_FIELDS.EMAIL.label}
                        value={user.userEmail}
                        validate={validateEmail}
                        onSave={(val) => handleFieldSave('userEmail', val)}
                      />
                      <InlineEditableField
                        label={PROFILE_FIELDS.PHONE.label}
                        value={user.userPhoneNumber}
                        validate={validatePhone}
                        inputProps={{ maxLength: PHONE_CONFIG.MAX_LENGTH }}
                        startAdornment={
                          <span
                            style={{
                              marginRight: 6,
                              color: '#888',
                              fontWeight: 500,
                            }}
                          >
                            {PHONE_CONFIG.PREFIX}
                          </span>
                        }
                        onSave={(val) => handleFieldSave('phoneNumber', val)}
                      />
                    </Stack>
                  </Box>
                </MotionPaper>

                {/* Account Type Card */}
                <MotionPaper
                  variants={cardVariants}
                  elevation={0}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  sx={{
                    borderRadius: { xs: 0, sm: 4 },
                    border: '1px solid',
                    borderColor: '#e5e7eb',
                    bgcolor: '#fff',
                    mb: { xs: 2, sm: 3 },
                    mx: { xs: 2, sm: 0 },
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.7)} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Business sx={{ color: '#fff', fontSize: 22 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '1.15rem',
                          fontWeight: 700,
                          color: '#1f2937',
                        }}
                      >
                        {t('profile.accountType')}
                      </Typography>
                    </Stack>

                    {!user?.userType || editingUserType ? (
                      <UserTypeSelector
                        userTypeDetails={userTypeDetails}
                        onUserTypeChange={async (val) => {
                          await handleUserTypeChange(val);
                          setEditingUserType(false);
                        }}
                        onLogout={handleLogout}
                        loading={loading}
                        error={errors.userType}
                      />
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2.5,
                            bgcolor: alpha(primaryColor, 0.06),
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: alpha(primaryColor, 0.15),
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#1f2937',
                                fontSize: '1.05rem',
                              }}
                            >
                              {isBusiness ? t('profile.business') : t('profile.individual')}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                color: '#6b7280',
                                mt: 0.5,
                              }}
                            >
                              {isBusiness ? t('profile.businessDesc') : t('profile.personalDesc')}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setEditingUserType(true)}
                            sx={{
                              borderColor: primaryColor,
                              color: primaryColor,
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                              '&:hover': {
                                borderColor: primaryColor,
                                bgcolor: alpha(primaryColor, 0.05),
                              },
                            }}
                          >
                            {t('profile.change')}
                          </Button>
                        </Box>
                      </motion.div>
                    )}
                    {isBusiness && (
                      <Box sx={{ mt: 2.5 }}>
                        <BusinessFields
                          user={user}
                          userTypeDetails={userTypeDetails}
                          onFieldSave={handleFieldSave}
                          themeColors={themeColors}
                          loading={loading}
                          error={errors.businessFields}
                        />
                      </Box>
                    )}
                  </Box>
                </MotionPaper>
              </Grid>

              {/* Right Column - Quick Actions as horizontal grid + Sign Out */}
              <Grid item xs={12} md={7}>
                <MotionPaper
                  variants={cardVariants}
                  elevation={0}
                  sx={{
                    borderRadius: { xs: 0, sm: 4 },
                    border: '1px solid',
                    borderColor: '#e5e7eb',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                    mx: { xs: 2, sm: 0 },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      sx={{
                        fontSize: '1.15rem',
                        fontWeight: 700,
                        color: '#1f2937',
                        mb: 2.5,
                      }}
                    >
                      {t('profile.quickActions')}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {quickActions.map((item, index) => (
                        <MotionBox
                          key={index}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 + index * 0.08 }}
                          whileHover={{
                            y: -3,
                            boxShadow: `0 6px 20px ${alpha(item.color, 0.15)}`,
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2.5,
                            borderRadius: 3,
                            bgcolor: '#f9fafb',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid transparent',
                            '&:hover': {
                              borderColor: alpha(item.color, 0.25),
                              bgcolor: alpha(item.color, 0.04),
                            },
                          }}
                          onClick={() => item.href !== '#' && (window.location.href = item.href)}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2.5,
                              bgcolor: alpha(item.color, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <item.icon sx={{ color: item.color, fontSize: 24 }} />
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: '#1f2937',
                                fontSize: '0.95rem',
                              }}
                            >
                              {item.label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {item.description}
                            </Typography>
                          </Box>
                          <ChevronRight sx={{ color: '#9ca3af', fontSize: 20, flexShrink: 0 }} />
                        </MotionBox>
                      ))}
                    </Box>
                  </Box>

                  {/* Logout Section */}
                  <Divider />
                  <Box sx={{ p: 3 }}>
                    <MotionBox whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={handleLogout}
                        startIcon={<Logout />}
                        sx={{
                          py: 1.5,
                          borderRadius: 2.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          borderWidth: 1.5,
                          '&:hover': {
                            borderWidth: 1.5,
                            bgcolor: alpha('#ef4444', 0.05),
                          },
                        }}
                      >
                        {t('profile.signOut')}
                      </Button>
                    </MotionBox>
                  </Box>
                </MotionPaper>
              </Grid>
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* Edit Modal */}
      <EditFieldModal
        open={editModal.open}
        field={editModal.field}
        value={editModal.field ? user[editModal.field.key] : ''}
        onSave={handleEditSave}
        onClose={handleEditClose}
        loading={editLoading}
        user={user}
      />

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmationDialog
        open={showLogoutDialog}
        onClose={cancelLogout}
        onConfirm={confirmLogout}
        loading={loggingOut}
      />
    </LayoutContainer>
  );
}
